import { useEffect, useState } from "react";
import api from "../lib/api";
import { Play } from "lucide-react";

// Extract YouTube ID from various URL formats
function youtubeId(url) {
  if (!url) return null;
  const patterns = [
    /youtu\.be\/([a-zA-Z0-9_-]{6,})/,
    /[?&]v=([a-zA-Z0-9_-]{6,})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{6,})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{6,})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

function youtubeThumb(id) {
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
}

export default function Gallery() {
  const [media, setMedia] = useState([]);
  const [filter, setFilter] = useState("all"); // all | photo | video
  const [open, setOpen] = useState(null); // selected media

  useEffect(() => {
    api.get("/media").then((r) => setMedia(r.data)).catch(() => {});
  }, []);

  const filtered = media.filter((m) => filter === "all" || m.type === filter);

  return (
    <div data-testid="gallery-page" className="pt-28 pb-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-xs uppercase tracking-[0.18em] text-[color:var(--inv-text-muted)] mb-3">
          Comunidad · Recuerdos
        </div>
        <h1 className="font-heading text-4xl md:text-6xl font-semibold tracking-tight leading-[1.05]">
          Galería
        </h1>
        <p className="mt-4 max-w-2xl text-[color:var(--inv-text-muted)]">
          Fotos y videos de los servicios, eventos y momentos especiales de
          nuestra iglesia.
        </p>

        <div className="mt-8 flex gap-2">
          {[
            { key: "all", label: "Todo" },
            { key: "photo", label: "Fotos" },
            { key: "video", label: "Videos" },
          ].map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`btn-pill text-sm py-2 ${
                filter === f.key ? "btn-pill-primary" : "btn-pill-secondary"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="mt-16 text-center text-[color:var(--inv-text-muted)]">
            Aún no hay {filter === "all" ? "contenido" : filter === "photo" ? "fotos" : "videos"}.
            <br />
            <span className="text-sm">
              El administrador puede agregar desde el panel.
            </span>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map((m) => {
              const ytId = m.type === "video" ? youtubeId(m.url) : null;
              const thumb = ytId ? youtubeThumb(ytId) : m.url;
              return (
                <button
                  key={m.id}
                  data-testid={`media-item-${m.id}`}
                  onClick={() => setOpen(m)}
                  className="group relative aspect-square rounded-2xl overflow-hidden bg-surface-alt hover-lift"
                >
                  <img
                    src={thumb}
                    alt={m.caption || ""}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {m.type === "video" && (
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                        <Play size={22} className="text-[color:var(--inv-secondary)] ml-1" fill="currentColor" />
                      </div>
                    </div>
                  )}
                  {m.caption && (
                    <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 to-transparent text-white text-xs text-left">
                      {m.caption}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Lightbox */}
        {open && (
          <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setOpen(null)}
            data-testid="media-lightbox"
          >
            <div
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {open.type === "video" ? (
                <div className="aspect-video w-full rounded-2xl overflow-hidden bg-black">
                  <iframe
                    src={`https://www.youtube.com/embed/${youtubeId(open.url)}?autoplay=1`}
                    title={open.caption || "video"}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <img
                  src={open.url}
                  alt={open.caption || ""}
                  className="max-h-[85vh] mx-auto rounded-2xl"
                />
              )}
              {open.caption && (
                <div className="mt-4 text-center text-white/80">{open.caption}</div>
              )}
              <button
                data-testid="close-lightbox"
                onClick={() => setOpen(null)}
                className="mt-4 mx-auto block btn-pill bg-white/10 text-white border border-white/20 text-sm"
              >
                Cerrar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
